'use client';

import { CheckCircle2, Loader2, Unplug } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';

interface SetupConnectedPlatformPanelProps {
  /** Which platform's labels to render (selects the `integrations:<platform>.*` keys). */
  platform: 'shopify' | 'woocommerce';
  /** Display name interpolated into the shared `{{platform}}` copy (e.g. "Shopify"). */
  platformName: string;
  /** Human-readable store/shop name shown in the connected confirmation. */
  shopName: string;
  /** Adds a subtle "(via OAuth)" marker when the connection was established through OAuth. */
  connectedViaOAuth?: boolean;
  /** Localised confirmation copy shown inside the disconnect dialog. */
  disconnectConfirm: string;
  onDisconnect: () => void;
  isDisconnecting: boolean;
}

/**
 * Connected-state body for a platform connect step (Shopify/WooCommerce). Mirrors the
 * integrations section: a success confirmation plus a guarded "Disconnect" action.
 */
export function SetupConnectedPlatformPanel({
  platform,
  platformName,
  shopName,
  connectedViaOAuth = false,
  disconnectConfirm,
  onDisconnect,
  isDisconnecting,
}: SetupConnectedPlatformPanelProps) {
  const { t } = useTranslation(['setup', 'integrations']);

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-start gap-3 rounded-[0.75rem] border border-green-200 bg-green-50 px-4 py-3.5">
        <CheckCircle2 className="mt-0.5 size-5 shrink-0 text-green-600" aria-hidden />
        <div className="flex min-w-0 flex-col gap-1">
          <p className="m-0 text-sm font-semibold text-green-900">
            {t(`integrations:${platform}.connectedTo`, { shopName })}
            {connectedViaOAuth ? (
              <span className="ml-1.5 text-xs font-normal text-green-700">
                {t(`integrations:${platform}.viaOAuth`)}
              </span>
            ) : null}
          </p>
          <p className="m-0 text-[0.8125rem] leading-[1.5] text-green-800/80">
            {t('setup:experience.connect.connected.description')}
          </p>
        </div>
      </div>

      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="text-destructive hover:text-destructive hover:bg-destructive/10 w-fit"
            disabled={isDisconnecting}
          >
            {isDisconnecting ? (
              <Loader2 className="mr-2 size-4 animate-spin" aria-hidden />
            ) : (
              <Unplug className="mr-2 size-4" aria-hidden />
            )}
            {isDisconnecting
              ? t(`integrations:${platform}.disconnecting`)
              : t(`integrations:${platform}.disconnect`)}
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('setup:experience.connect.connected.confirmTitle', { platform: platformName })}
            </AlertDialogTitle>
            <AlertDialogDescription>{disconnectConfirm}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDisconnecting}>
              {t('setup:experience.connect.connected.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => onDisconnect()}
              disabled={isDisconnecting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t(`integrations:${platform}.disconnect`)}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
